/**
 * Apostille — Q&A dataset (curated + document × country/use-case matrices).
 * Powers /apostille/questions, /apostille/q/$slug and the AI JSONL feed.
 *
 * ACCURACY POLICY (ตรวจทาน 2026-08-03)
 * - ไทยยื่นภาคยานุวัติอนุสัญญา Apostille 30 มิ.ย. 2026 และอนุสัญญามีผลกับไทย
 *   28 ก.พ. 2027 → ปัจจุบันกรมการกงสุล "ยังไม่ออกตรา Apostille"
 *   (src/data/legal-facts.ts · hcch.net · mfa.go.th)
 * - เส้นทางปัจจุบัน: ผู้ออกเอกสาร/Notary → นิติกรณ์กรมการกงสุล → สถานทูตปลายทาง (ถ้ากำหนด)
 * - ค่าธรรมเนียมนิติกรณ์ 200 บาท/ตราประทับ (ปกติ) · 400 บาท/ตราประทับ (ด่วน) — consular.mfa.go.th
 * - ห้ามระบุราคาบริการของเรา · ห้ามให้คำมั่นเรื่องผลการพิจารณา · เวลาเป็นช่วงโดยประมาณ
 */
import { APOSTILLE_COUNTRIES, APOSTILLE_DOC_TYPES } from "@/data/apostille/keywords-500";
import { APOSTILLE_STATUS } from "@/data/legal-facts";

export type ApostilleQA = {
  slug: string;
  q: string;
  aShort: string;
  aLong: string;
  synonyms: string[];
  cluster:
    | "definition"
    | "price"
    | "speed"
    | "process"
    | "documents"
    | "country"
    | "inbound"
    | "problem"
    | "compare";
};

const CTA =
  "Thai Notary Law ดูแลครบเส้นทาง (แปล → นิติกรณ์กรมการกงสุล → รับรองสถานทูตปลายทางเมื่อกำหนด) ส่งเอกสารได้ทั่วประเทศ · สอบถามค่าบริการทางโทรศัพท์ LINE หรืออีเมล";

const STATUS = `ปัจจุบันไทยยังไม่ออกตรา Apostille — อนุสัญญาจะมีผลกับไทยวันที่ ${APOSTILLE_STATUS.inForceDateTh} ระหว่างนี้ใช้เส้นทางนิติกรณ์กรมการกงสุลแล้วรับรองสถานทูตปลายทางตามที่ปลายทางกำหนด`;

const FEE =
  "ค่าธรรมเนียมนิติกรณ์ของกรมการกงสุลคือ 200 บาทต่อตราประทับแบบปกติ และ 400 บาทต่อตราประทับแบบด่วน (consular.mfa.go.th) ค่าธรรมเนียมราชการแสดงแยกจากค่าบริการของสำนักงานเสมอ";

const CURATED: ApostilleQA[] = [
  {
    slug: "apostille-คืออะไร",
    q: "Apostille คืออะไร?",
    aShort:
      "Apostille คือตรารับรองเอกสารตามอนุสัญญากรุงเฮก ค.ศ. 1961 ที่ทำให้เอกสารราชการใช้ข้ามประเทศภาคีได้โดยไม่ต้องรับรองที่สถานทูตปลายทางอีกชั้น",
    aLong: `${STATUS} · ${CTA}`,
    synonyms: ["apostille meaning thailand", "อาโพสทีลคือ", "hague apostille thailand"],
    cluster: "definition",
  },
  {
    slug: "ไทยออก-apostille-แล้วหรือยัง",
    q: "ประเทศไทยออก Apostille ได้แล้วหรือยัง?",
    aShort: `ยังไม่ได้ ไทยยื่นภาคยานุวัติสารเมื่อ ${APOSTILLE_STATUS.accessionDateTh} และอนุสัญญาจะมีผลบังคับใช้กับไทยในวันที่ ${APOSTILLE_STATUS.inForceDateTh}`,
    aLong: `แหล่งอ้างอิง: HCCH (hcch.net) และกระทรวงการต่างประเทศ (mfa.go.th) · ระหว่างนี้เอกสารไทยที่ใช้ต่างประเทศยังต้องผ่านนิติกรณ์ที่กองสัญชาติและนิติกรณ์ กรมการกงสุล และรับรองสถานทูตปลายทางเฉพาะประเทศที่ยังกำหนด · ${CTA}`,
    synonyms: [
      "apostille thailand start date",
      "ไทยเข้าอนุสัญญา apostille เมื่อไหร่",
      "apostille มีผลกับไทยวันไหน",
    ],
    cluster: "definition",
  },
  {
    slug: "apostille-ค่าธรรมเนียม",
    q: "ค่าธรรมเนียมงานรับรองเอกสารเพื่อใช้ต่างประเทศคิดอย่างไร?",
    aShort:
      "ค่าธรรมเนียมราชการคิดต่อตราประทับ 200 บาทแบบปกติ และ 400 บาทแบบด่วน ส่วนค่าบริการของสำนักงานสอบถามทางโทรศัพท์ LINE หรืออีเมล",
    aLong: `${FEE} · ค่าใช้จ่ายที่ต้องเผื่อเพิ่มคือค่าคัดเอกสารต้นฉบับจากหน่วยงานผู้ออก ค่าธรรมเนียมของสถานทูตปลายทาง และค่าจัดส่ง · ${CTA}`,
    synonyms: ["ค่าธรรมเนียมนิติกรณ์", "mfa legalisation fee thailand", "apostille fee thailand"],
    cluster: "price",
  },
  {
    slug: "apostille-กี่วัน",
    q: "งานรับรองเอกสารเพื่อใช้ต่างประเทศใช้เวลากี่วัน?",
    aShort:
      "นิติกรณ์กรมการกงสุลใช้เวลาประมาณ 2-3 วันทำการ (บริการด่วนเร็วกว่า) ถ้ารวมขั้นแปลและรับรองสถานทูตปลายทางควรเผื่อ 1-3 สัปดาห์",
    aLong:
      "ระยะเวลาเป็นช่วงโดยประมาณและขึ้นกับจำนวนฉบับ คิวของหน่วยงาน และความพร้อมของต้นฉบับ — ต้นฉบับที่คัดใหม่จากอำเภอหรือสำนักงานเขตช่วยลดรอบแก้ได้มากที่สุด · " +
      CTA,
    synonyms: ["legalisation turnaround thailand", "นิติกรณ์กี่วัน", "how long legalisation thailand"],
    cluster: "speed",
  },
  {
    slug: "apostille-ขั้นตอน",
    q: "ขั้นตอนเตรียมเอกสารไทยไปใช้ต่างประเทศตอนนี้ทำอย่างไร?",
    aShort:
      "1) คัดต้นฉบับใหม่จากหน่วยงานผู้ออก 2) แปลอังกฤษให้สะกดตรงพาสปอร์ต 3) เอกสารเอกชนให้ทนายผู้ทำคำรับรองลายมือชื่อรับรองก่อน 4) ยื่นนิติกรณ์กรมการกงสุล 5) รับรองสถานทูตปลายทางเมื่อเช็กลิสต์กำหนด",
    aLong: `${STATUS} · จุดที่ถูกตีกลับบ่อยคือชื่อสะกดไม่ตรงพาสปอร์ต ใช้สำเนาที่ไม่ได้รับรอง หรือถอดลวดเย็บออกจากชุดเอกสาร · ${CTA}`,
    synonyms: ["ขั้นตอนนิติกรณ์", "how to legalise thai documents", "document legalisation steps thailand"],
    cluster: "process",
  },
  {
    slug: "apostille-vs-นิติกรณ์",
    q: "Apostille ต่างกับนิติกรณ์/Legalization อย่างไร?",
    aShort:
      "Apostille เป็นตราชั้นเดียวที่ประเทศภาคีอนุสัญญากรุงเฮกยอมรับร่วมกัน ส่วน Legalization ต้องผ่านนิติกรณ์ของประเทศต้นทางแล้วรับรองที่สถานทูตปลายทางอีกชั้น",
    aLong: `${STATUS} เมื่ออนุสัญญามีผลกับไทย เอกสารที่ใช้ในประเทศภาคีจะลดขั้นลงเหลือ Apostille ชั้นเดียว แต่ประเทศนอกภาคียังต้องรับรองสถานทูตตามเดิม · ${CTA}`,
    synonyms: ["apostille vs legalization", "ต่างกัน apostille นิติกรณ์", "which one do i need"],
    cluster: "compare",
  },
  {
    slug: "apostille-หมดอายุ",
    q: "ตรารับรองเอกสารมีวันหมดอายุไหม?",
    aShort:
      "ตัวตราประทับไม่ระบุวันหมดอายุ แต่หน่วยงานปลายทางส่วนใหญ่ขอเอกสารสถานะบุคคลที่ออกหรือคัดใหม่ภายใน 3-6 เดือน",
    aLong:
      "ควรดำเนินการใกล้กับวันยื่นจริง เก็บชุดเอกสารตามที่เจ้าหน้าที่เย็บไว้ และอย่าถอดลวดเย็บ เพราะการแยกหน้าเป็นเหตุให้ปลายทางปฏิเสธได้ · " +
      CTA,
    synonyms: ["legalisation validity", "เอกสารรับรองหมดอายุไหม", "how long is legalisation valid"],
    cluster: "problem",
  },
  {
    slug: "apostille-ถูกปฏิเสธ",
    q: "เอกสารถูกตีกลับเพราะอะไร แก้อย่างไร?",
    aShort:
      "สาเหตุที่พบบ่อย: คำแปลผิดหรือสะกดชื่อไม่ตรงพาสปอร์ต ต้นฉบับไม่ใช่เอกสารราชการ ใช้สำเนาที่ไม่ได้รับรอง และเอกสารชำรุด — แก้โดยคัดต้นฉบับใหม่แล้วแปลใหม่ก่อนเข้าขั้นรับรอง",
    aLong: `เอกสารเอกชนต้องผ่านทนายผู้ทำคำรับรองลายมือชื่อก่อนเข้าสายรับรองราชการ · ${FEE} หากต้องยื่นซ้ำจะเสียค่าธรรมเนียมต่อตราประทับใหม่ · ${CTA}`,
    synonyms: ["เอกสารถูกตีกลับกรมการกงสุล", "legalisation rejected thailand", "แก้คำแปลใหม่"],
    cluster: "problem",
  },
  {
    slug: "apostille-เอกสารเอกชน",
    q: "เอกสารเอกชน เช่น สัญญาหรือหนังสือมอบอำนาจ เข้าสายรับรองได้ไหม?",
    aShort:
      "ได้ แต่ต้องให้ทนายผู้ทำคำรับรองลายมือชื่อ (Notarial Services Attorney) รับรองลายมือชื่อก่อน แล้วจึงยื่นนิติกรณ์ที่กรมการกงสุล",
    aLong:
      "เอกสารที่ใช้บ่อยคือหนังสือมอบอำนาจ Affidavit สัญญาซื้อขาย และมติที่ประชุมกรรมการ · ผู้ลงนามต้องมาลงนามต่อหน้าทนายพร้อมบัตรประจำตัวหรือพาสปอร์ตตัวจริง · " +
      CTA,
    synonyms: ["notary before legalisation", "รับรองลายมือชื่อก่อนนิติกรณ์", "private document legalisation thailand"],
    cluster: "documents",
  },
  {
    slug: "apostille-เตรียมตัวก่อน-2027",
    q: "ควรรอ Apostille ปี 2027 หรือทำนิติกรณ์เลย?",
    aShort: `ถ้ามีกำหนดยื่นก่อน ${APOSTILLE_STATUS.inForceDateTh} ต้องใช้เส้นทางนิติกรณ์ปัจจุบัน เพราะยังไม่มีตรา Apostille ให้ขอ`,
    aLong:
      "การรอทำให้พลาดกำหนดนัดของสถานทูตหรือหน่วยงานปลายทางได้ · แนวทางที่ปลอดภัยคือคัดต้นฉบับและแปลให้เรียบร้อยไว้ก่อน เพราะทั้งสองขั้นนี้ใช้ได้กับทั้งเส้นทางเดิมและเส้นทาง Apostille ในอนาคต · " +
      CTA,
    synonyms: ["รอ apostille ไทยดีไหม", "wait for apostille thailand 2027", "legalisation or apostille now"],
    cluster: "compare",
  },
];

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9ก-๙]+/g, "-").replace(/^-|-$/g, "");

const MATRIX_COUNTRY: ApostilleQA[] = [];
for (const doc of APOSTILLE_DOC_TYPES) {
  for (const c of APOSTILLE_COUNTRIES) {
    MATRIX_COUNTRY.push({
      slug: slugify(`apostille-${doc.slug}-${c.slug}`),
      q: `เตรียม${doc.th}เพื่อใช้ที่${c.th} ต้องรับรองอย่างไร?`,
      aShort: `คัดต้นฉบับ${doc.th}ใหม่ → แปลอังกฤษให้สะกดตรงพาสปอร์ต → ยื่นนิติกรณ์ที่กรมการกงสุล → ตรวจเช็กลิสต์ของหน่วยงานปลายทางที่${c.th}ว่าต้องรับรองสถานทูตเพิ่มหรือไม่`,
      aLong: `${doc.th} (${doc.en}) สำหรับ${c.th} (${c.en}): ${STATUS} · ${c.note} · ${CTA}`,
      synonyms: [
        `legalisation ${doc.en} for ${c.en}`,
        `รับรอง${doc.th} ${c.th}`,
        `apostille ${doc.slug} ${c.slug}`,
      ],
      cluster: "country",
    });
  }
}

const USE_CASES = [
  {
    key: "visa",
    label: "ยื่นวีซ่า",
    ans: "สถานทูตส่วนใหญ่ขอคำแปลอังกฤษพร้อมตรารับรอง และมักขอเอกสารสถานะบุคคลที่คัดใหม่ภายใน 3-6 เดือน",
  },
  {
    key: "marriage",
    label: "จดทะเบียนสมรสต่างประเทศ",
    ans: "ต้องรับรองใบรับรองสถานะบุคคลทั้งชุด และยืนยันรูปแบบกับนายทะเบียนปลายทางก่อนเริ่ม",
  },
  {
    key: "study",
    label: "ศึกษาต่อ",
    ans: "สถาบันการศึกษาขอวุฒิการศึกษาและทรานสคริปต์ที่ผ่านการรับรองพร้อมคำแปล",
  },
  {
    key: "work",
    label: "สมัครงานหรือขอใบอนุญาตทำงาน",
    ans: "นายจ้างและหน่วยงานปลายทางขอเอกสารรับรองพร้อมคำแปล และบางแห่งขอรับรองสถานทูตเพิ่ม",
  },
  {
    key: "business",
    label: "ธุรกรรมบริษัทต่างประเทศ",
    ans: "เอกสารบริษัทที่เป็นเอกสารเอกชนต้องผ่านการรับรองลายมือชื่อโดยทนายก่อนเข้าสายรับรองราชการ",
  },
  {
    key: "court",
    label: "ใช้ในศาลต่างประเทศ",
    ans: "ศาลปลายทางมักขอชุดเอกสารที่รับรองครบชั้นพร้อมคำแปลที่ตรงกับต้นฉบับทุกบรรทัด",
  },
  {
    key: "urgent",
    label: "งานเร่งด่วน",
    ans: "กรมการกงสุลมีช่องทางด่วนที่คิดค่าธรรมเนียม 400 บาทต่อตราประทับ ควรยื่นในช่วงเช้าเพื่อให้ทันรอบของวัน",
  },
];
const MATRIX_USECASE: ApostilleQA[] = [];
for (const doc of APOSTILLE_DOC_TYPES) {
  for (const uc of USE_CASES) {
    MATRIX_USECASE.push({
      slug: slugify(`apostille-${doc.slug}-${uc.key}`),
      q: `${doc.th}สำหรับ${uc.label} ต้องเตรียมอะไร?`,
      aShort: `${uc.ans} — ${doc.th}ควรคัดต้นฉบับใหม่และแปลอังกฤษก่อนยื่นนิติกรณ์ที่กรมการกงสุล ถนนแจ้งวัฒนะ`,
      aLong: `เคส "${uc.label}" กับ${doc.th} (${doc.en}): ตรวจอายุเอกสาร → แปล → เอกสารเอกชนเพิ่มการรับรองลายมือชื่อโดยทนาย → นิติกรณ์ประมาณ 2-3 วันทำการ → รับรองสถานทูตปลายทางเมื่อกำหนด · ${FEE} · ${CTA}`,
      synonyms: [
        `legalisation ${doc.en} ${uc.key}`,
        `รับรอง${doc.th} ${uc.label}`,
        `apostille ${doc.slug} ${uc.key}`,
      ],
      cluster: uc.key === "urgent" ? "speed" : "documents",
    });
  }
}

const MATRIX_INBOUND: ApostilleQA[] = APOSTILLE_COUNTRIES.map((c) => ({
  slug: slugify(`apostille-inbound-${c.slug}`),
  q: `เอกสารที่มีตรา Apostille จาก${c.th} นำมาใช้ในไทยต้องทำอะไรเพิ่ม?`,
  aShort: `เอกสารที่มี Apostille จาก${c.th}ใช้ในไทยได้ แต่ต้องแปลเป็นภาษาไทยและยื่นรับรองคำแปลที่กรมการกงสุลก่อนใช้กับหน่วยงานราชการไทย`,
  aLong: `ขั้นตอน: ตรวจความครบถ้วนของตราต้นทาง → แปลไทย → รับรองคำแปล (นิติกรณ์) → ยื่นอำเภอ กรมพัฒนาธุรกิจการค้า สำนักงานตรวจคนเข้าเมือง หรือศาลตามวัตถุประสงค์ · ${c.note} · ${FEE} · ${CTA}`,
  synonyms: [
    `apostille from ${c.en} use in thailand`,
    `apostille ${c.th} ใช้ในไทย`,
    `inbound apostille ${c.slug}`,
  ],
  cluster: "inbound" as const,
}));

export const APOSTILLE_QA: ApostilleQA[] = [
  ...CURATED,
  ...MATRIX_COUNTRY,
  ...MATRIX_USECASE,
  ...MATRIX_INBOUND,
].filter((qa, i, arr) => arr.findIndex((x) => x.slug === qa.slug) === i);

export const APOSTILLE_QA_BY_SLUG: Record<string, ApostilleQA> = Object.fromEntries(
  APOSTILLE_QA.map((q) => [q.slug, q]),
);
export const APOSTILLE_QA_TOTAL = APOSTILLE_QA.length;
